import React, { useState, useEffect } from 'react';
import { ExternalLink } from 'lucide-react';
import useDocumentTitle from '../hooks/useDocumentTitle';

export const LandingPage = () => {
  const [hoveredCard, setHoveredCard] = useState(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  // Set page title
  useDocumentTitle('G.F.T Investments | Financial Infrastructure for the Next Era');

  const products = [
    {
      name: 'app.bharatbit.world',
      url: 'https://app.bharatbit.world',
      description: 'Digital asset exchange and OTC infrastructure for secure, high-trust execution.',
      dominant: true
    },
    {
      name: 'dhee.world',
      url: 'https://dhee.world',
      description: 'Financial intelligence layer for capital flow, market regimes, and opportunity detection.'
    },
    {
      name: 'ti-e.world',
      subtitle: 'by taskOS',
      fullName: 'task intelligence engine',
      url: 'https://ti-e.world',
      description: 'Task intelligence systems transforming ideas into structured execution.'
    },
    {
      name: 'ac-e.world',
      subtitle: 'by aceOS',
      fullName: 'account command engine',
      url: 'https://ac-e.world',
      description: 'Autonomous execution architecture for next-generation financial operations.'
    }
  ];

  // Mouse parallax effect
  useEffect(() => {
    const handleMouseMove = (e) => {
      const x = (e.clientX / window.innerWidth - 0.5) * 20;
      const y = (e.clientY / window.innerHeight - 0.5) * 20;
      setMousePosition({ x, y });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  // Enhanced particle system with more visible particles
  useEffect(() => {
    const canvas = document.getElementById('particles-canvas');
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const particles = [];
    const particleCount = 60;

    for (let i = 0; i < particleCount; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        radius: Math.random() * 2 + 0.8,
        speedX: (Math.random() - 0.5) * 0.4,
        speedY: (Math.random() - 0.5) * 0.4,
        opacity: Math.random() * 0.5 + 0.2,
        hue: Math.random() > 0.5 ? 220 : 270
      });
    }

    function animate() {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      particles.forEach(particle => {
        particle.x += particle.speedX;
        particle.y += particle.speedY;

        if (particle.x < 0 || particle.x > canvas.width) particle.speedX *= -1;
        if (particle.y < 0 || particle.y > canvas.height) particle.speedY *= -1;

        ctx.beginPath();
        ctx.arc(particle.x, particle.y, particle.radius, 0, Math.PI * 2);
        const gradient = ctx.createRadialGradient(
          particle.x, particle.y, 0,
          particle.x, particle.y, particle.radius * 3
        );
        gradient.addColorStop(0, `hsla(${particle.hue}, 70%, 60%, ${particle.opacity})`);
        gradient.addColorStop(1, `hsla(${particle.hue}, 70%, 60%, 0)`);
        ctx.fillStyle = gradient;
        ctx.fill();
      });

      requestAnimationFrame(animate);
    }

    animate();

    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div className="system-container">
      {/* Background Layers */}
      <canvas id="particles-canvas" className="particles-layer"></canvas>
      <div className="grid-layer"></div>
      <div className="glow-layer glow-top-left"></div>
      <div className="glow-layer glow-bottom-right"></div>

      {/* Central Energy Core */}
      <div className="central-energy-core"></div>

      {/* Enhanced Connection Network */}
      <svg className="connection-network" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" style={{ stopColor: 'rgba(100, 150, 255, 0.6)', stopOpacity: 1 }} />
            <stop offset="50%" style={{ stopColor: 'rgba(147, 51, 234, 0.8)', stopOpacity: 1 }} />
            <stop offset="100%" style={{ stopColor: 'rgba(100, 150, 255, 0.6)', stopOpacity: 1 }} />
          </linearGradient>
          <filter id="glow">
            <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
        </defs>
        {/* Horizontal connections */}
        <line x1="33%" y1="48%" x2="67%" y2="48%" className="flow-line flow-1" filter="url(#glow)" />
        <line x1="33%" y1="52%" x2="67%" y2="52%" className="flow-line flow-2" filter="url(#glow)" />
        {/* Vertical connections */}
        <line x1="48%" y1="38%" x2="48%" y2="62%" className="flow-line flow-3" filter="url(#glow)" />
        <line x1="52%" y1="38%" x2="52%" y2="62%" className="flow-line flow-4" filter="url(#glow)" />
        {/* Diagonal connections */}
        <line x1="35%" y1="42%" x2="65%" y2="58%" className="flow-line flow-5" filter="url(#glow)" />
        <line x1="35%" y1="58%" x2="65%" y2="42%" className="flow-line flow-6" filter="url(#glow)" />
        {/* Central cross */}
        <circle cx="50%" cy="50%" r="4" className="central-node" filter="url(#glow)" />
      </svg>

      {/* Header */}
      <header className="system-header">
        <div className="logo-container">
          <img src="/gft-logo.png" alt="G.F.T." className="logo-image" />
          <sup className="trademark-symbol">®</sup>
        </div>
        <a href="mailto:info@gftworld.in" className="contact-email">info@gftworld.in</a>
      </header>

      {/* Main System Interface */}
      <main className="system-main">
        {/* Compact Hero */}
        <div className="hero-system" style={{
          transform: `translate(${mousePosition.x * 0.02}px, ${mousePosition.y * 0.02}px)`
        }}>
          <div className="hero-energy-core"></div>
          <div className="hero-glow-backdrop"></div>
          
          <div className="company-label">G.F.T. Investments Private Limited</div>
          <h1 className="system-title">Building Financial Infrastructure for the Next Era</h1>
          <p className="system-subtitle">
            A unified ecosystem of platforms across market intelligence, execution systems, and digital asset infrastructure.
          </p>
        </div>

        {/* Compact About + Ecosystem Inline */}
        <div className="compact-info-section">
          <div className="about-compact">
            <h2 className="about-heading-compact">About G.F.T.</h2>
            <p className="about-text-compact">
              G.F.T. Investments Private Limited (Gold Fintech Investments Pvt Ltd) is a technology-driven company building advanced financial systems across digital asset exchanges, market intelligence platforms, and execution engines for global markets.
            </p>
          </div>
          <div className="ecosystem-compact">
            An integrated platform ecosystem spanning intelligence, execution, and market access.
          </div>
        </div>

        {/* System Nodes Grid */}
        <div className="system-grid">
          {products.map((product, index) => (
            <a
              key={index}
              href={product.url}
              target="_blank"
              rel="noopener noreferrer"
              className={`system-node ${product.dominant ? 'dominant-node' : ''} ${hoveredCard === index ? 'hovered' : ''} float-${index + 1}`}
              onMouseEnter={() => setHoveredCard(index)}
              onMouseLeave={() => setHoveredCard(null)}
              style={{
                transform: `translate(${mousePosition.x * (0.05 + index * 0.01)}px, ${mousePosition.y * (0.05 + index * 0.01)}px)`
              }}
            >
              <div className="node-energy-field"></div>
              <div className="node-glow-aura"></div>
              <div className="node-depth-shadow"></div>
              <div className="border-trace"></div>
              <div className="border-trace-2"></div>
              <div className="node-glass-content">
                <div className="node-header">
                  <div className="node-title-container">
                    <h3 className="node-title">{product.name}</h3>
                    {product.subtitle && (
                      <span className="node-subtitle">{product.subtitle}</span>
                    )}
                    {product.fullName && (
                      <span className="node-fullname">({product.fullName})</span>
                    )}
                  </div>
                  <ExternalLink 
                    size={18} 
                    className={`node-link-icon ${hoveredCard === index ? 'active' : ''}`}
                  />
                </div>
                <p className="node-description">{product.description}</p>
              </div>
            </a>
          ))}
        </div>
      </main>

      {/* Footer */}
      <footer className="system-footer">
        <p>© 2019-2026 G.F.T. Investments Private Limited. All rights reserved.</p>
      </footer>
    </div>
  );
};
